import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

interface MarkdownContentProps {
  content: string;
}

const allowedElements = [
  "p",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "a",
  "br",
  "h1",
  "h2",
  "h3",
  "h4",
  "blockquote",
  "code",
  "pre",
  "hr",
];

function mergeClassName(base: string, extra?: string): string {
  return [base, extra].filter(Boolean).join(" ");
}

const markdownComponents: Components = {
  a: ({ node, className, ...props }) => (
    <a
      {...props}
      className={mergeClassName("text-accent underline", className)}
      target="_blank"
      rel="noreferrer"
    />
  ),
  ul: ({ node, className, ...props }) => (
    <ul
      {...props}
      className={mergeClassName("list-disc space-y-1 pl-5", className)}
    />
  ),
  ol: ({ node, className, ...props }) => (
    <ol
      {...props}
      className={mergeClassName("list-decimal space-y-1 pl-5", className)}
    />
  ),
  li: ({ node, className, ...props }) => (
    <li
      {...props}
      className={mergeClassName("text-sm leading-relaxed", className)}
    />
  ),
  h1: ({ node, className, ...props }) => (
    <h1
      {...props}
      className={mergeClassName(
        "text-base font-semibold text-slate-800",
        className,
      )}
    />
  ),
  h2: ({ node, className, ...props }) => (
    <h2
      {...props}
      className={mergeClassName(
        "text-base font-semibold text-slate-800",
        className,
      )}
    />
  ),
  h3: ({ node, className, ...props }) => (
    <h3
      {...props}
      className={mergeClassName(
        "text-sm font-semibold text-slate-800",
        className,
      )}
    />
  ),
  h4: ({ node, className, ...props }) => (
    <h4
      {...props}
      className={mergeClassName(
        "text-sm font-semibold text-slate-800",
        className,
      )}
    />
  ),
  blockquote: ({ node, className, ...props }) => (
    <blockquote
      {...props}
      className={mergeClassName(
        "border-l-2 border-slate-200 pl-3 text-sm italic text-slate-600",
        className,
      )}
    />
  ),
  code: ({ inline, className, ...props }: any) => {
    if (inline) {
      return (
        <code
          {...props}
          className={mergeClassName(
            "rounded bg-slate-100 px-1 py-0.5 text-[13px]",
            className,
          )}
        />
      );
    }
    return (
      <code
        {...props}
        className={mergeClassName(
          "block rounded bg-slate-900/80 px-3 py-2 text-[13px] text-white",
          className,
        )}
      />
    );
  },
  hr: () => <hr className="border-slate-200" />,
};

export function MarkdownContent({ content }: MarkdownContentProps) {
  if (!content?.trim()) {
    return <p className="text-sm text-slate-500">暂无总结</p>;
  }

  return (
    <ReactMarkdown
      allowedElements={allowedElements}
      remarkPlugins={[remarkGfm, remarkBreaks]}
      className="space-y-2 text-sm leading-relaxed text-slate-700"
      components={markdownComponents}
    >
      {content}
    </ReactMarkdown>
  );
}
